<template>
  <el-dialog
    v-el-drag-dialog
    :visible.sync="innerVisible"
    :title="$t('SystemMaintenance.SetFieldValueSynchronization')"
    width="1000px"
    :close-on-click-modal="false"
  >
    <div style="display: flex;flex-direction: column;gap: 10px;">
      <div>
        <el-button :disabled="!gridIsEdit" type="primary" @click="onNew">{{ $t('CargoMsg.Cargo_New') }}</el-button>
        <el-button :disabled="!gridIsEdit" type="danger" @click="onDelete">{{ $t('CargoMsg.Cargo_Delete') }}</el-button>
        <el-checkbox v-model="valueSyncManage.useHqSet" style="margin: 0 5px;" @change="onUseHqSet" />
        <span>{{ $t('CargoMsg.Cargo_GeneralSettings') }}</span>
      </div>
      <cargo-ag-grid
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :row-data="valueSyncList"
        style="min-height: 400px;"
        @gridReady="params=>{gridApi=params.api}"
      />
    </div>
    <span slot="footer">
      <el-button type="primary" @click="okHandler">{{ $t('Message.Confirm') }}</el-button>
      <el-button @click="innerVisible=false">{{ $t('Message.Cancel') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import CargoAgGridSelectEditor from '@/components/Cargo/Common/CargoAgGridSelectEditor'
import CargoAgGridAdvanceSelectEditor from '@/components/Cargo/Common/CargoAgGridAdvanceSelectEditor'
import { getValueSyncManage, saveValueSyncManage, getBranchElementBlockList } from '@/api/sysManage/uiCustom'
import { getLoading4Cxt } from '@/utils/utilBaseElement'
import * as UICustomConstants from '@/class/cargo/model/parameters/UICustomConstants'
import { basicCacheKeys } from '@/utils/cacheConfig'
import { getCacheListByKey } from '@/utils/basicDataCache'

export default {
  name: 'ValueSyncSetDialog',
  props: {
    visible: { type: Boolean, required: true },
    module: { type: Number, required: true },
    branchId: { type: Number, required: true }
  },
  data() {
    return {
      innerVisible: false,
      gridOptions: {
        rowSelection: 'multiple',
        suppressCellSelection: true,
        suppressRowClickSelection: true,
        singleClickEdit: true,
        frameworkComponents: {
          CargoAgGridSelectEditor,
          CargoAgGridAdvanceSelectEditor
        },
        context: {
          componentParent: this
        }
      },
      gridApi: null,
      officeList: [],
      pageList: [], // 页签列表
      pageFieldsDict: {}, // 页签关联字段列表
      valueSyncManage: { useHqSet: true },
      hqValueSyncList: null,
      valueSyncList: [],
      deletedValueSyncKeys: []
    }
  },
  computed: {
    // 表格是否可编辑
    gridIsEdit() {
      return this.branchId === 1 || !this.valueSyncManage.useHqSet
    },
    columnDefs() {
      return [
        {
          headerName: '',
          field: '',
          width: 65,
          headerCheckboxSelection: true,
          checkboxSelection: true,
          sortable: false,
          suppressMenu: true,
          suppressSizeToFit: true,
          resizable: false
        },
        {
          headerName: this.$t('SystemMaintenance.SourceTab'),
          field: 'sourcePage',
          width: 125,
          valueFormatter: params => {
            return this.getPageLabel(params.value)
          },
          editable: this.gridIsEdit,
          cellEditor: 'CargoAgGridAdvanceSelectEditor',
          cellEditorParams: {
            selectProps: {
              tagValue: 'pageType',
              tagName: 'pageName',
              staticOptions: this.pageList,
              filterable: true,
              filterTagNames: ['pageName']
            }
          },
          onCellValueChanged: params => this.onCellChange(params)
        },
        {
          headerName: this.$t('SystemMaintenance.SourceField'),
          field: 'sourceField',
          width: 200,
          valueFormatter: params => {
            return this.getFieldLabel(params.data.sourcePage, params.value)
          },
          editable: this.gridIsEdit,
          cellEditor: 'CargoAgGridAdvanceSelectEditor',
          cellEditorParams: params => {
            const data = params.data
            let staticOptions = this.pageFieldsDict[data.sourcePage] || []
            if (data.sourcePage === data.writePage) {
              staticOptions = staticOptions.filter(p => p.fieldName !== data.writeField)
            }
            // 限制可选字段
            staticOptions = staticOptions.filter(
              p => ['etd'].includes(p.fieldName)
            )
            return {
              selectProps: {
                tagValue: 'fieldName',
                tagName: 'fieldLabel',
                staticOptions,
                filterable: true,
                filterTagNames: ['fieldLabel']
              }
            }
          }
        },
        {
          headerName: this.$t('SystemMaintenance.WriteTab'),
          field: 'writePage',
          width: 125,
          valueFormatter: params => {
            return this.getPageLabel(params.value)
          },
          editable: this.gridIsEdit,
          cellEditor: 'CargoAgGridAdvanceSelectEditor',
          cellEditorParams: {
            selectProps: {
              tagValue: 'pageType',
              tagName: 'pageName',
              staticOptions: this.pageList,
              filterable: true,
              filterTagNames: ['pageName']
            }
          },
          onCellValueChanged: params => this.onCellChange(params)
        },
        {
          headerName: this.$t('SystemMaintenance.WriteField'),
          field: 'writeField',
          width: 200,
          valueFormatter: params => {
            return this.getFieldLabel(params.data.writePage, params.value)
          },
          editable: this.gridIsEdit,
          cellEditor: 'CargoAgGridAdvanceSelectEditor',
          cellEditorParams: params => {
            const data = params.data
            let staticOptions = this.pageFieldsDict[data.writePage] || []
            if (data.writePage === data.sourcePage) {
              staticOptions = staticOptions.filter(p => p.fieldName !== data.sourceField)
            }
            // 限制可选字段
            staticOptions = staticOptions.filter(
              p => ['customsDate'].includes(p.fieldName)
            )
            return {
              selectProps: {
                tagValue: 'fieldName',
                tagName: 'fieldLabel',
                staticOptions,
                filterable: true,
                filterTagNames: ['fieldLabel']
              }
            }
          }
        },
        {
          headerName: this.$t('SystemMaintenance.SyncOnlyAtCreation'),
          field: 'syncOnlyCreate',
          width: 130,
          valueFormatter: params => {
            return params.value ? this.$t('Message.Yes') : this.$t('Message.No')
          },
          editable: this.gridIsEdit,
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              style: 'width: 100%'
            },
            options: [{ value: true, label: this.$t('Message.Yes') }, { value: false, label: this.$t('Message.No') }],
            tagName: 'label',
            tagValue: 'value'
          }
        },
        {
          headerName: this.$t('SystemMaintenance.SynchronizedReceiptWithMainTicket'),
          field: 'masterSyncSlave',
          width: 130,
          valueFormatter: params => {
            return params.value ? this.$t('Message.Yes') : this.$t('Message.No')
          },
          editable: this.gridIsEdit,
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              style: 'width: 100%'
            },
            options: [{ value: true, label: this.$t('Message.Yes') }, { value: false, label: this.$t('Message.No') }],
            tagName: 'label',
            tagValue: 'value'
          }
        }
      ]
    }
  },
  watch: {
    visible: {
      immediate: true,
      async handler(newVal) {
        this.innerVisible = newVal
        if (newVal) {
          const loading = getLoading4Cxt(this)
          try {
            await this.loadPageData()
            this.valueSyncManage = await getValueSyncManage({ module: this.module, branchIds: this.branchId }).then(res => res.data[0])
            this.hqValueSyncList = this.valueSyncManage.hqUiCustomValueSyncList || null
            this.valueSyncList = this.valueSyncManage.uiCustomValueSyncList
            this.onUseHqSet()
          } finally {
            loading.close()
          }
        }
      }
    },
    innerVisible(newVal) {
      this.$emit('update:visible', newVal)
      if (!newVal) {
        this.pageList = []
        this.pageFieldsDict = {}
        this.valueSyncManage = { useHqSet: false }
        this.hqValueSyncList = null
        this.valueSyncList = []
        this.deletedValueSyncKeys = []
      }
    }
  },
  created() {
    this.officeList = getCacheListByKey(basicCacheKeys.basic_data_Office)
  },
  methods: {
    async loadPageData() {
      switch (this.module) {
        case UICustomConstants.SEA_BOOKING_MODULE:
          this.pageList.push({ pageType: UICustomConstants.SEA_BOOKING_BASIC, pageName: this.$t('SystemMaintenance.BasicTab') })
          break
      }
      const promiseList = []
      for (const page of this.pageList) {
        const promise = getBranchElementBlockList({ pageType: page.pageType, branchId: 1 }).then(res => {
          const blocks = res.data.branchBlocks || res.data.hqBlocks || res.data.sysBlocks
          const fieldInfoList = []
          for (const block of blocks) {
            for (const element of block.uiCustomElementList) {
              if (element.uiCustomFieldList) {
                for (const field of element.uiCustomFieldList) {
                  fieldInfoList.push({ fieldName: field.fieldDict.fieldName, fieldLabel: field.customLabel || field.sysLabel })
                }
              }
            }
          }
          this.pageFieldsDict[page.pageType] = fieldInfoList
        })
        promiseList.push(promise)
      }
      await Promise.all(promiseList)
    },
    getPageLabel(pageType) {
      return this.pageList.find(p => p.pageType === pageType)?.pageName || ''
    },
    getFieldLabel(pageType, fieldName) {
      return this.pageFieldsDict[pageType]?.find(p => p.fieldName === fieldName)?.fieldLabel || ''
    },
    onCellChange(params) {
      const { node, data } = params
      const filed = params.colDef.field
      switch (filed) {
        case 'sourcePage':
          data.sourceField = ''
          node.setDataValue('sourceField', data.sourceField)
          break
        case 'writePage':
          data.writeField = ''
          node.setDataValue('writeField', data.writeField)
          break
      }
    },
    onUseHqSet() {
      if (this.branchId === 1) {
        return
      }
      if (this.valueSyncManage.useHqSet) {
        for (const item of this.valueSyncList) {
          if (item.uuid) {
            this.deletedValueSyncKeys.push(item.uuid)
          }
        }
        this.valueSyncList = Array.from(this.hqValueSyncList, p => {
          return {
            valueSyncManageUuid: '',
            sourcePage: p.sourcePage,
            sourceField: p.sourceField,
            writePage: p.writePage,
            writeField: p.writeField,
            syncOnlyCreate: p.syncOnlyCreate,
            masterSyncSlave: p.masterSyncSlave
          }
        })
      }
    },
    onNew() {
      this.valueSyncList.push({
        valueSyncManageUuid: '',
        sourcePage: 0,
        sourceField: '',
        writePage: 0,
        writeField: '',
        syncOnlyCreate: false,
        masterSyncSlave: false
      })
    },
    async onDelete() {
      const rows = this.gridApi.getSelectedRows()
      if (rows.length === 0) {
        this.$message.warning(this.$t('common.tips.hasOneAtleast'))
        return
      }
      const flag = await this.$confirm(this.$t('SystemMaintenance.AreWantToDeleteData'), this.$t('Message.Prompt'), {
        confirmButtonText: this.$t('common.btn.ok'),
        cancelButtonText: this.$t('common.btn.cancel'),
        closeOnClickModal: false,
        type: 'warning'
      }).then(() => true).catch(() => false)
      if (!flag) {
        return
      }
      for (const row of rows) {
        if (row.uuid) {
          this.deletedValueSyncKeys.push(row.uuid)
        }
      }
      this.valueSyncList = this.valueSyncList.filter(p => !rows.includes(p))
    },
    async okHandler() {
      let valueSyncList = this.valueSyncList
      if (this.valueSyncManage.useHqSet && this.branchId !== 1) {
        for (const item of valueSyncList) {
          if (item.uuid) {
            this.deletedValueSyncKeys.push(item.uuid)
          }
        }
        valueSyncList = []
      } else {
        for (const item of valueSyncList) {
          if (!item.sourcePage) {
            this.$message.warning(this.$t('SystemMaintenance.TheFieldCannotBeEmpty', [this.$t('SystemMaintenance.SourceTab')]))
            return
          }
          if (!item.sourceField) {
            this.$message.warning(this.$t('SystemMaintenance.TheFieldCannotBeEmpty', [this.$t('SystemMaintenance.SourceField')]))
            return
          }
          if (!item.writePage) {
            this.$message.warning(this.$t('SystemMaintenance.TheFieldCannotBeEmpty', [this.$t('SystemMaintenance.WriteTab')]))
            return
          }
          if (!item.writeField) {
            this.$message.warning(this.$t('SystemMaintenance.TheFieldCannotBeEmpty', [this.$t('SystemMaintenance.WriteField')]))
            return
          }
        }
      }
      const flag = await this.$confirm(this.$t('SystemMaintenance.ConfirmFieldValueSynchronizationConfiguration'), this.$t('Message.Prompt'), {
        confirmButtonText: this.$t('common.btn.ok'),
        cancelButtonText: this.$t('common.btn.cancel'),
        closeOnClickModal: false,
        type: 'warning'
      }).then(() => true).catch(() => false)
      if (!flag) {
        return
      }
      const loading = getLoading4Cxt(this)
      saveValueSyncManage({
        ...this.valueSyncManage,
        hqUiCustomValueSyncList: null,
        uiCustomValueSyncList: valueSyncList,
        deletedValueSyncKeys: this.deletedValueSyncKeys
      }).then(() => {
        this.$message.success(this.$t('Message.SaveSuccessfully'))
        this.innerVisible = false
        this.updateGlobalUICustom(this.module, this.branchId)
      }).finally(() => {
        loading.close()
      })
    },
    updateGlobalUICustom(module, branchId) {
      if (this.$store.state.uiCustom.enable) {
        this.$store.dispatch('uiCustom/updateModuleSyncSetInfo', { module, branchIds: branchId === 1 ? Array.from(this.officeList, p => p.officeId) : [branchId] })
      }
    }
  }
}
</script>
